/* ============================================================
   NORTHSHEEP — IN-GRID COLLECTION PROMO CARDS

   Places collection promos into the product grid at intervals, so a long
   collection gets a break from unbroken rows of products.

   WHY JS RATHER THAN LIQUID
   The grid is rebuilt underneath us constantly: lazy paging appends a second
   batch, curate's re-flow rebuilds it when a product is hidden, and the client
   filters swap it out entirely. A promo emitted inside the Liquid product loop
   would survive none of those. Liquid renders the cards into an inert
   <template>; this decides how many to show, and where.

   HOW MANY — KEEPING THE GRID COMPLETE
   A promo occupies `span` grid cells (2 columns, or the whole row on a
   single-column layout). The grid ends on a full row only when

       (products + span x promos) % columns == 0

   A fixed promo count can't satisfy that for every column count: at 4 columns
   two cells only ever land on even multiples, so an odd number of promos is
   permanently 2 cells short; at 3 columns only multiples of 3 work. So the
   COUNT adapts instead — each page shows the largest number of promos that
   leaves the grid complete, and the remainder is carried to the next page.

   At 96 products that gives:
       4 columns: 1->0  2->2  3->2  4->4     (deferring 1, 0, 1, 0)
       3 columns: 1->0  2->0  3->3  4->3
       2 columns: always all of them — every count already fits
   The final page of a collection shows everything left over: its product count
   is an arbitrary remainder, so it can never be a full grid regardless.

   PLACEMENT
   Two columns wide means a promo can only START where two columns are free —
   at a row boundary, or `columns - span` into a row. Alternating between those
   two is what stops it marching down one edge.
   ============================================================ */

(function () {
  'use strict';

  /* #ProductGridContainer is the stable anchor. Dawn's facets.js applies every
     sort and native filter by doing
         ProductGridContainer.innerHTML = <fresh server HTML>
     which throws away #product-grid entirely and builds a new one. Holding a
     reference to the grid — and hanging an observer off it — meant this script
     went silently dead the moment anyone sorted or filtered, taking the promos
     and the lazily-loaded products with it. So everything below re-acquires
     the grid instead of capturing it once. */
  var container = document.getElementById('ProductGridContainer');
  if (!container) return;

  var grid = null;
  var allPromos = [];
  var FIRST_AFTER = 12;
  var EVERY = 24;
  var PAGE_SIZE = 48;
  var BATCHES = 1;
  var TOTAL_SHOPIFY_PAGES = 1;
  var FULL_PAGE_PRODUCTS = 48;
  var TOTAL_VISUAL_PAGES = 1;
  var MAX_PER_PAGE = 0; // 0 = no cap; set from data-ns-promo-per-page

  var placing = false;
  var observer = null;
  var pendingAfterDrag = false;

  /* Point at the current grid, re-reading everything that came from Liquid.
     Returns false when there's nothing to work with (an empty result set, or
     promos switched off for this view). */
  function acquire() {
    var g = document.getElementById('product-grid');
    var tpl = document.querySelector('[data-ns-promo-template]');

    if (!g || !tpl) { grid = null; return false; }
    if (g === grid) return allPromos.length > 0;

    grid = g;
    allPromos = Array.prototype.slice.call(tpl.content.querySelectorAll('.ns-promo'));

    FIRST_AFTER = parseInt(g.getAttribute('data-ns-promo-first'), 10) || 12;
    EVERY = parseInt(g.getAttribute('data-ns-promo-every'), 10) || 24;
    PAGE_SIZE = parseInt(g.getAttribute('data-ns-page-size'), 10) || 48;
    BATCHES = parseInt(g.getAttribute('data-ns-lazy-batches'), 10) || 1;
    TOTAL_SHOPIFY_PAGES = parseInt(g.getAttribute('data-ns-total-pages'), 10) || 1;
    /* THE MERCHANT'S "PROMOS PER PAGE", WHICH THIS FILE USED TO IGNORE.

       The section has always emitted it and nothing here ever read it, so the
       only limit on a page was whether the grid squared up. On a DESKTOP that
       is a real limit — four columns, a two-cell promo, so only an even number
       fits — and the fault stayed hidden. On a PHONE the grid is two columns
       and a promo spans both, so EVERY count squares: page 1 was handed the
       entire list, and every later page got nothing. The server had rendered
       tiles into those pages correctly and this file then removed them again,
       which is exactly what the owner kept seeing and I kept failing to
       reproduce on a desktop (owner, 21 Sep 2026: "always just the first page
       and then the cards stop never they continue on page 2").

       0 means no cap, which keeps the old behaviour for any grid that has not
       set one. */
    MAX_PER_PAGE = parseInt(g.getAttribute('data-ns-promo-per-page'), 10) || 0;
    FULL_PAGE_PRODUCTS = PAGE_SIZE * BATCHES;
    TOTAL_VISUAL_PAGES = Math.max(1, Math.ceil(TOTAL_SHOPIFY_PAGES / BATCHES));

    if (observer) observer.disconnect();
    observer = new MutationObserver(function () { schedule(); });
    /* WATCH THE CARDS, NOT JUST THE GRID.

       Without `subtree` this only saw attributes on the grid element itself,
       so a filter hiding ninety cards one by one changed nothing it was
       listening to and the promos were never re-spaced — they sat at the
       slots the unfiltered page had given them while the cards between them
       collapsed, which is the row of promo tiles with no products in front
       of it. `hidden` is the attribute the client-side filters actually set
       (ns-collection-filters.js), so it has to be in the filter list beside
       `class`.

       schedule() debounces at 120ms, which is what makes this affordable:
       hiding ninety cards is ninety mutations and one re-place. */
    observer.observe(grid, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'hidden'],
    });

    return allPromos.length > 0;
  }

  function visualPage() {
    if (window.NsLazyPages && window.NsLazyPages.visualPage) {
      return window.NsLazyPages.visualPage;
    }
    var p = parseInt(new URLSearchParams(location.search).get('page'), 10) || 1;
    return Math.ceil(p / BATCHES);
  }

  function flushOwnRecords() {
    if (observer) observer.takeRecords();
  }

  /* Only cards actually taking up a grid cell.

     Hidden products are display:none, so they occupy nothing — counting them
     made the "is the grid complete" arithmetic use a product total that didn't
     match what was on screen. Cards mid-gesture are lifted out of flow too. */
  /* One definition of "takes up a grid cell", used by BOTH the desired-slot
     calculation and the measurement of where things actually are. When these
     two disagreed, the no-op guard misfired and promos drifted. */
  function countsAsCell(li) {
    /* `hidden` is how the client-side filters take a card out of the grid
       (ns-collection-filters.js sets it for favourites, size, sale and the
       hidden-products filter). It was missing from this list, and that is the
       wall of promo tiles the owner photographed on 21 Sep 2026.

       Counting a card nobody can see meant the slots were still worked out
       over all 96 products, so the promos stayed at DOM positions 12, 36, 60
       and 84 while the cards between them collapsed to nothing — four tiles
       landing back to back with no products at all in front of them.

       Promos still stay visible under an active filter; that is deliberate
       and is not what changes here. What changes is that they are now spaced
       against the cards a shopper can actually see, and the
       "cards.length <= FIRST_AFTER" guard below finally does what its own
       comment always claimed — a narrow filter shows no promos rather than
       mostly promos. */
    return !li.hidden
      && !li.classList.contains('ns-curate-hidden')
      && !li.classList.contains('ns-fav-dragging')
      && !li.classList.contains('ns-curate-swiping');
  }

  /* Select by "grid item that is not a promo", NOT by data-ns-card-handle.

     That attribute is only emitted when the card-reorder feature is on
     (main-collection-product-grid.liquid renders it inside {% if ns_reorder %}).
     Keying off it silently coupled promos to an unrelated feature: with reorder
     switched off this returned an empty list, place() bailed at the
     "cards.length <= FIRST_AFTER" guard, and the promo cards never appeared at
     all — no error, nothing in the console, just a setting that did nothing. */
  function productCards() {
    return Array.prototype.slice
      .call(grid.querySelectorAll(':scope > li.grid__item:not([data-ns-promo])'))
      .filter(countsAsCell);
  }

  /* Column count, derived from geometry rather than by walking the first row.

     Row-walking looked reasonable but was fragile: a card being hidden or
     animated briefly reports a zero-size rect, and the walk could then finish
     the row early and report 3 columns on a 4-column grid. That fed
     (96 + 2k) % 3 == 0 and produced a third promo out of nowhere.

     Grid width divided by card pitch can't be fooled that way — one visible
     card and the gap are enough, and both are stable mid-animation. */
  function columnCount(cards) {
    if (!cards.length) return 1;

    var gridW = grid.getBoundingClientRect().width;
    var gap = parseFloat(getComputedStyle(grid).columnGap) || 0;
    var cardW = 0;

    for (var i = 0; i < cards.length; i++) {
      var r = cards[i].getBoundingClientRect();
      if (r.width > 1 && r.height > 1) { cardW = r.width; break; }
    }

    if (!gridW || !cardW) return 1;

    var cols = Math.round((gridW + gap) / (cardW + gap));
    return Math.max(1, Math.min(cols, cards.length));
  }

  /* The most promos this page can show while still ending on a full row.
     Pure — used to replay earlier pages, so it must not depend on history. */
  function countForPage(products, cols, span, available, isLastPage) {
    if (available <= 0) return 0;
    /* Never more than the merchant asked for. Without this the only limit was
       the grid squaring up, which on a two-column phone is no limit at all —
       see MAX_PER_PAGE above. */
    var cap = MAX_PER_PAGE > 0 ? Math.min(available, MAX_PER_PAGE) : available;

    // The last page's product count is whatever remains, so it can't be
    // squared anyway — show everything rather than hold promos back forever.
    if (isLastPage) return cap;

    for (var k = cap; k >= 0; k--) {
      if ((products + span * k) % cols === 0) return k;
    }
    return -1; // nothing squares it
  }

  /* Count for the page we're actually rendering.

     Hiding a product briefly leaves an odd product total, and at 4 columns an
     odd total can't be squared by ANY number of two-cell promos. Taken
     literally that means "show none" — so a single hide made every promo blink
     out, collapsing the grid by a row, and the replacement landing put it back.
     That flicker was the jump when hiding near the bottom of the page.

     The count is therefore held steady through states that can't be squared:
     they only ever last until the refill lands, and nothing can be tidy during
     them anyway. */
  var lastSettledCount = null;
  function countForCurrentPage(products, cols, span, available, isLastPage) {
    var k = countForPage(products, cols, span, available, isLastPage);

    if (k >= 0) {
      lastSettledCount = k;
      return k;
    }

    if (lastSettledCount !== null) return Math.min(lastSettledCount, available);
    return available; // never settled yet — showing them beats showing none
  }

  /* This page's slice of the promo list.

     Pages render independently, so the offset is recovered by replaying the
     same decision for each earlier page — deterministic, because every page
     before the last holds exactly FULL_PAGE_PRODUCTS products. That's what
     stops a promo appearing twice, or never. */
  function shareForThisPage(cols, span, actualProducts) {
    var page = visualPage();
    var remaining = allPromos.length;
    var offset = 0;

    for (var p = 1; p < page; p++) {
      // Earlier pages always hold a full batch, so these always square.
      var taken = countForPage(FULL_PAGE_PRODUCTS, cols, span, remaining, p === TOTAL_VISUAL_PAGES);
      if (taken < 0) taken = 0;
      offset += taken;
      remaining -= taken;
    }

    var mine = countForCurrentPage(actualProducts, cols, span, remaining, page === TOTAL_VISUAL_PAGES);
    return allPromos.slice(offset, offset + mine);
  }

  /* Product index at or after `from` where a promo can start without wrapping.
     A promo already placed shifts everything after it, so row position is
     (products so far + cells eaten by earlier promos) % cols. */
  function findSlot(from, cards, cols, side, cellsUsed, span) {
    var wanted = side === 1 && cols >= 3 ? cols - span : 0;
    for (var i = from; i < cards.length; i++) {
      if ((i + cellsUsed) % cols === wanted) return i;
    }
    return -1;
  }

  /* Where the promos SHOULD sit. Pure calculation, touches no DOM, so it can
     be compared against reality before anything is rebuilt. */
  function desiredSlots(cards, cols, span, count) {
    var out = [];
    var used = 0;
    var target = FIRST_AFTER;
    var side = 0;
    var cellsUsed = 0;

    while (used < count && target < cards.length) {
      var slot = findSlot(target, cards, cols, side, cellsUsed, span);
      if (slot < 1 || slot >= cards.length) break;

      var landedOff = (slot + cellsUsed) % cols;
      out.push({ slot: slot, side: landedOff === 0 ? 'left' : 'right' });

      used++;
      cellsUsed += span;
      side = landedOff === 0 ? 1 : 0;
      target = slot + EVERY;
    }

    return out;
  }

  /* Where they ACTUALLY sit, same shape — measured as products preceding each. */
  function actualSlots() {
    var out = [];
    var products = 0;
    var n = grid.firstElementChild;

    while (n) {
      if (n.hasAttribute('data-ns-promo')) {
        out.push({ slot: products, side: n.getAttribute('data-ns-promo-side') });
      } else if (n.matches('li.grid__item') && countsAsCell(n)) {
        /* Only cards that actually occupy a grid cell, matching
           productCards() exactly — by the same selector too (audit, 2 Oct
           2026): this one still required data-ns-card-handle, which only some
           settings print, so without it the two never agreed and every tile
           was re-inserted every 450 ms.

           Counting hidden cards here made this disagree with desiredSlots(),
           which never counts them: straight after a hide both reported "36
           products before promo 2", so the no-op guard concluded nothing had
           changed — while on screen the promo had really slid a cell earlier,
           because the hidden card had stopped taking up space. That mismatch
           is why the promo drifted left and only snapped back later. */
        products++;
      }
      n = n.nextElementSibling;
    }

    return out;
  }

  function serverTilesRight(want, actual, tiles, share) {
    if (want.length !== actual.length || tiles.length !== want.length) return false;
    for (var i = 0; i < want.length; i++) {
      if (want[i].slot !== actual[i].slot) return false;
      if (actual[i].side != null && actual[i].side !== want[i].side) return false;
      if (!share[i] || tiles[i].getAttribute('data-ns-promo-id') !== share[i].getAttribute('data-ns-promo-id')) return false;
    }
    return true;
  }

  function sameSlots(a, b) {
    if (a.length !== b.length) return false;
    for (var i = 0; i < a.length; i++) {
      if (a[i].slot !== b[i].slot || a[i].side !== b[i].side) return false;
    }
    return true;
  }

  function clearPromos() {
    Array.prototype.forEach.call(grid.querySelectorAll(':scope > [data-ns-promo]'), function (el) {
      el.parentNode.removeChild(el);
    });
  }

  function dragging() {
    return document.body.classList.contains('ns-fav-sorting');
  }

  /* Is a client-side filter (favourites / size / hidden view) driving the grid?

     Under a filter the grid is a RESULT SET, not a browse: it's a different
     number of products, it isn't paginated the same way, and the filter engine
     rebuilds it from its own card list — which has no promos in it, so they
     were being wiped anyway. Offering "browse this collection" cards in the
     middle of someone's filtered results is the wrong moment for them.

     The "Reset hidden" pill is deliberately ignored: it's just an affordance
     shown whenever anything is hidden, not a filter, and treating it as one
     would remove the promos permanently after a single hide. */
  function filterActive() {
    var pills = document.querySelectorAll('[data-ns-pill-remove]');
    for (var i = 0; i < pills.length; i++) {
      if (pills[i].getAttribute('data-ns-pill-remove') !== 'reset-hidden') return true;
    }
    return false;
  }

  /* force=true bypasses the mid-drag guard.

     A swipe-to-hide happens WHILE the body carries ns-fav-sorting, so the
     guard below was skipping the very re-place that keeps a promo still —
     it drifted a cell with the row and snapped back when the gesture ended.
     The hide handler calls this explicitly, in the same frame, so it must be
     allowed through. */
  function place(force) {
    var didMutate = true; // set false by the no-op guard below
    if (placing) return;

    /* Never re-place mid-drag. Dragging a product shuffles siblings on every
       pointer move, which changes how many products precede each promo;
       re-placing on each made the promos twitch, so they looked like they were
       reacting to the dragged card. Sit still, re-place once after the drop. */
    if (!force && dragging()) {
      pendingAfterDrag = true;
      return;
    }

    if (!acquire()) return;

    /* NOTE: promos are DELIBERATELY kept under an active client-side filter.

       filterActive() exists, and an audit flagged it as a bug that it is never
       called — the argument being that a filtered grid is a result set, not a
       browse. That is a reasonable default and it is not what this store wants:
       the promos are how a shopper moves between related collections, and they
       are most useful, not least, when someone has narrowed to four hides and
       wants to see what else there is.

       The count still comes from the cards actually present, and the
       "cards.length <= FIRST_AFTER" guard above means a small result set shows
       none at all — so a narrow filter does not end up mostly promos. Left in
       place intentionally; do not "fix" it. */

    placing = true;

    try {
      var cards = productCards();

      /* NEVER RE-PLACE FROM A GRID THAT CANNOT BE MEASURED.

         Every slot below is derived from columnCount(), which divides the
         grid's width by a card's width. When the grid has no width — it is
         still being laid out, or something has hidden it, and the grid IS
         hidden deliberately by the ns-order-pending guard until a saved card
         order has been applied — that function falls back to a single column
         and this pass then "corrects" a perfectly good layout using a column
         count that was never true. The promos end up bunched at the top of
         the grid, which is what the owner photographed on 21 Sep 2026.

         Doing nothing is not a degraded outcome here, it is the right one:
         the server renders the promos in their correct slots, so an aborted
         pass leaves them exactly where they belong. The observers and the
         load handler call this again once there is something real to
         measure. */
      var gridWidth = grid.getBoundingClientRect().width;
      if (!gridWidth) {
        didMutate = false;
        return;
      }

      if (cards.length <= FIRST_AFTER) {
        /* Only a pass that removed tiles earns a re-check (audit, 2 Oct 2026).
           This return left didMutate true, so a short grid — a filter with 12
           or fewer results, a short last page — re-ran every 450 ms for as long
           as the page was open. */
        didMutate = !!grid.querySelector(':scope > [data-ns-promo]');
        if (didMutate) clearPromos();
        return;
      }

      var cols = columnCount(cards);
      var span = Math.min(2, cols);
      var share = shareForThisPage(cols, span, cards.length);
      var want = desiredSlots(cards, cols, span, share.length);

      /* Bail out when they're already exactly where they belong.

         This is what stops the page yanking you upward while scrolling. Every
         lazily-loaded batch fires a mutation, and rebuilding in response
         removes promos ABOVE the viewport — which collapses the document,
         defeats the browser's scroll anchoring, and throws you back up the
         page. Appending products at the END can't move a promo at position 12,
         so the correct response is to do nothing at all. */
      var actual = actualSlots();
      if (sameSlots(want, actual)) { didMutate = false; return; }

      /* THE SERVER'S TILES, ALREADY RIGHT (audit, 2 Oct 2026). Tiles printed by
         the server carry no side yet - nothing styles it, it is this file's
         own note - so the check above never matched on a page's first pass:
         every tile was taken out and put back on every load, plus a verify
         pass. When the slots and the promos are the ones wanted, the sides are
         noted and nothing moves. */
      var tiles = grid.querySelectorAll(':scope > [data-ns-promo]');
      if (serverTilesRight(want, actual, tiles, share)) {
        for (var s = 0; s < tiles.length; s++) tiles[s].setAttribute('data-ns-promo-side', want[s].side);
        didMutate = false;
        return;
      }

      /* Move the promos that are already here; only build the ones that aren't.

         This used to clear every promo and clone fresh ones on each pass, and
         that is what made a hide at the bottom of the page look wrong. A clone
         carries brand-new <img> elements, so the images had to be fetched and
         decoded all over again — and until they were, the promo cell was
         short. Hiding a product rebuilt the promos, so every row below the
         first promo collapsed by the missing image height and sprang back a
         moment later once it decoded. Measured on the cowhide collection: 90
         of 96 cards dropped 86px and returned.

         The new card at the end was fading in correctly the whole time. But
         the ground moved underneath it, so it read as a card descending into
         place from above.

         Reusing the nodes means the images are never touched, the rows keep
         their height, and nothing below the promo moves at all. */
      var have = {};
      Array.prototype.forEach.call(grid.querySelectorAll(':scope > [data-ns-promo]'), function (el) {
        var id = el.getAttribute('data-ns-promo-id');
        // A duplicate of one we've already claimed is surplus — drop it.
        if (id && !have[id]) have[id] = el;
        else if (el.parentNode) el.parentNode.removeChild(el);
      });

      var wanted = {};
      for (var i = 0; i < want.length; i++) {
        var pid = share[i].getAttribute('data-ns-promo-id');
        var node = (pid && have[pid]) || share[i].cloneNode(true);
        if (pid) wanted[pid] = 1;
        node.setAttribute('data-ns-promo-side', want[i].side);
        grid.insertBefore(node, cards[want[i].slot] || null);
      }

      // Promos this page no longer wants (the count adapts per page).
      Object.keys(have).forEach(function (id) {
        if (!wanted[id] && have[id].parentNode) have[id].parentNode.removeChild(have[id]);
      });
    } finally {
      flushOwnRecords();
      placing = false;
      /* Only re-check when this pass actually MOVED something.

         verifyAfterSettle() used to run unconditionally, including after the
         no-op guard above returned — so the re-check scheduled another pass,
         which no-op'd and scheduled another, for the life of the page. Two
         forced layouts a second (columnCount does getBoundingClientRect +
         getComputedStyle, actualSlots walks every sibling) on a 96-card grid,
         forever, draining battery on mobile for nothing.

         A settle-check is only meaningful after a real change; if nothing
         moved there is nothing that could still be settling. */
      if (didMutate) verifyAfterSettle();
      else verifyStreak = 0;
    }
  }

  /* One delayed re-check after the grid stops moving.

     A pass can run mid-animation — while a card is being hidden, or before the
     layout settles — and produce an arrangement that was right for that
     instant. Nothing mutates afterwards, so no observer fires and the wrong
     count sticks. Cheap to repeat now that place() is a no-op when the promos
     are already correct, so this can't cause churn or move the scroll. */
  var verifying = false, verifyStreak = 0;
  function verifyAfterSettle() {
    /* At most three re-checks in a row (audit, 2 Oct 2026): a layout that never
       agrees with itself must not keep the page busy for good. A pass that
       changes nothing starts the count again. */
    if (verifying || verifyStreak >= 3) return;
    verifying = true;
    verifyStreak++;
    setTimeout(function () {
      verifying = false;
      place();
    }, 450);
  }

  var t = null;
  function schedule() {
    clearTimeout(t);
    t = setTimeout(place, 120);
  }

  /* Resize must re-run: crossing a breakpoint changes the column count, which
     changes both how many promos fit and where they may sit. */
  var rt = null;
  function scheduleForced() {
    clearTimeout(rt);
    rt = setTimeout(place, 160);
  }

  /* The grid's own observer (childList for lazy batches / re-flow / filtering,
     class for the density toggle) is attached in acquire(), because it has to
     be re-attached every time Dawn hands us a new grid.

     This one watches the CONTAINER, which Dawn never replaces — it's how we
     notice that a sort or native filter just swapped the grid out. */
  new MutationObserver(function () { schedule(); }).observe(container, { childList: true });

  /* The gesture engine drops ns-fav-sorting from <body> when a card is
     released — a class change on an element we aren't otherwise watching. */
  new MutationObserver(function () {
    if (!dragging() && pendingAfterDrag) {
      pendingAfterDrag = false;
      place();
    }
  }).observe(document.body, { attributes: true, attributeFilter: ['class'] });

  /* Width changes only: the column count — the only thing a resize can
     change here — follows the width, and a phone toolbar collapsing mid-scroll
     fires resize with only the height changed (audit, 23 Sep 2026). */
  var lastResizeW = window.innerWidth;
  window.addEventListener('resize', function () {
    if (window.innerWidth === lastResizeW) return;
    lastResizeW = window.innerWidth;
    scheduleForced();
  });
  window.addEventListener('orientationchange', scheduleForced);
  window.addEventListener('load', scheduleForced);

  /* PLACE NOW IF THE GRID IS ALREADY THERE.

     This waited for DOMContentLoaded whenever the document was still parsing
     — which, now that the tag sits immediately after the grid closes, is
     always. So every bit of work done to get this file loaded early was
     thrown away: the tiles still went in after the whole document had parsed,
     and after the browser had painted and restored the scroll. Cards below a
     tile then jumped a cell, which is precisely what the owner saw: "if i
     back and forth on desktop above the promo card it doesnt shift ... when i
     reload behind a promo block then its flashing".

     The grid is the only thing this needs, and by the time this script runs
     it has been parsed. Waiting for the rest of the document buys nothing. */
  if (document.getElementById('product-grid')) {
    place();
  } else if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', place);
  } else {
    place();
  }

  window.NsCollectionPromos = {
    // Always forced: callers ask for this precisely when they need the promos
    // corrected immediately, gesture in progress or not.
    replace: function () { place(true); },
    available: allPromos.length,
    visualPage: visualPage
  };
})();
